import Vue from 'vue'
import axios from 'axios'
import config from '@/config'
import alert from '@/helpers/alert'
import router from '@/router'

const state = {
  leases: [],
  selected: {}
}

const getters = {
  getAll(state) {
    return state.leases
  },
  getLease: (state) => {
    return state.selected
  },
  getTenant: (state) => {
    // We are only supporting one tenant per lease atm
    if (state.selected.tenant && state.selected.tenant.length >0) {
      return state.selected.tenant[0];
    }else{
      alert.error('Lease: Missing tenant information')
      return {}
    }
    // return (state.selected.tenant.length > 0)
    //   ? state.selected.tenant[0]
    //   : {}

  },
  getPayments: (state) => {
    return state.selected.payment
  },
  getPaymentById: (state) => (id) => {
    const payment = state.selected.payment.filter(p => p.id === id)
    return payment.length > 0 ? payment[0] : {}
  }
}

const mutations = {
  /**
   * Sets the entire data set of leases
   * Good to use when you want to fill with new leases
   */
  setLeases(state, leases) {
    Vue.set(state, 'leases', leases)
  },
  /**
   * Updates an existing lease in the store
   */
  updateLease(state, data) {
    state.leases.forEach(lease => {
      if (lease.id === data.id) {
        Object.assign(lease, data)
      }
    })
    if (state.selected.id === data.id) {
      Object.assign(state.selected, data)
    }
  },
  /**
   * Adds a new lease to the list of leases
   */
  addLease(state, data) {
    state.leases.push(data)
  },
  deleteLease(state, id) {
    state.leases.forEach((lease, index) => {
      if (lease.id === id) {
        state.leases.splice(index, 1)
      }
    })
  },
  setSelected(state, data) {
    state.selected = data
  },
  /**
   * Merges the new payments data with updated payments
   *
   * @param {object} state
   * @param {object} payments updated payments
   */
  updatePayments(state, payments) {
    const existingPayments = state.selected.payment
    // Keep a map of ids so we can track the ids to update
    const updatedPaymentsIds = payments.map(p => p.id)

    // Loop through the existing payments and merge the new data
    existingPayments.forEach(ep => {
      if (updatedPaymentsIds.indexOf(ep.id) !== -1) {
        Object.assign(ep, payments[updatedPaymentsIds.indexOf(ep.id)])
      }
    })

    state.payment = existingPayments
  }
}

const actions = {
  /**
   * Makes an api request to get all leases for a house
   * Stores this data in the store
   */
  async getAll(context, id) {
    try {
      const response = await axios.get(
        config.apiBaseUrl + 'lease',
        { params: { house_id: id } }
      )

      if (response.data.errno === 0) {
        context.commit('setLeases', response.data.data)
      } else {
        alert.error(response.data.errmsg)
      }
    } catch (error) {
      alert.error(error)
    }
  },
  /**
   * Sends a new lease request to the backend
   * Saves the data to store if the request is successful
   */
  async addLease(context, data) {
    try {
      const response = await axios.post(config.apiBaseUrl + 'lease', data)
      let lease = data

      if (response.data.errno === 0) {
        lease.id = response.data.data.id
        context.commit('addLease', lease)
        alert.success('New lease successfully added')
        router.push({
          name: 'houseDetailLeasesInfo',
          params: { id: lease.id }
        })
      } else {
        alert.error(response.data.errmsg)
      }
    } catch (error) {
      alert.error(error)
    }
  },
  async getLease(context, id) {
    try {
      const response = await axios.get(
        config.apiBaseUrl + 'lease',
        { params: { id: id} }
      )

      if (response.data.errno === 0) {
        context.commit('setSelected', response.data.data)
      } else {
        alert.error(response.data.errmsg)
      }
    } catch (error) {
      alert.error(error)
    }
  },
  async updateLease(context, data) {
    try {
      const patchData = {...data};
      delete patchData.task;
      const response = await axios.patch(config.apiBaseUrl + 'lease', patchData)
      let lease = data

      if (response.data.errno === 0) {
        lease.id = response.data.data.id
        context.commit('updateLease', lease)
        alert.success('Lease updated successfully')
      } else {
        alert.error(response.data.errmsg)
      }
    } catch (error) {
      alert.error(error)
    }
  },
  async deleteLease(context, id) {
    try {
      const response = await axios.delete(
        config.apiBaseUrl + 'lease',
        {data: { id: id }}
      )

      if (response.data.errno === 0) {
        context.commit('deleteLease', id)
        alert.success('Lease deleted successfully')
      } else {
        alert.error(response.data.errmsg)
      }
    } catch (error) {
      alert.error(error.toString())
    }
  },
  async saveTenants(context, {leaseId, tenant}) {
    try {
      let response = {}
      tenant.lease_id = leaseId

      if (tenant.id !== '') {
        response = await axios.put(
          config.apiBaseUrl + 'tenant',
          tenant
        )
      } else {
        response = await axios.post(
          config.apiBaseUrl + 'tenant',
          tenant
        )
      }

      if (response.data.errno === 0) {
        context.dispatch('getLease', leaseId)
        alert.success('Tenant details saved successfully')
      } else {
        alert.error(response.data.errmsg)
      }
    } catch (error) {
      alert.error(error.toString())
    }
  },
  async uploadFile(context, data) {
    const formData = new FormData()
    formData.append('file', data.file, data.file.name)
    formData.append('type', 'tenant')
    formData.append('house_id', data.houseId)
    formData.append('tenant_id', data.tenantId)
    formData.append('lease_id', data.leaseId)

    try {
      const response = await axios.post(
        config.apiBaseUrl + 'house/uploadContractFile',
        formData
      )

      if (response.data.errno === 0) {
        let lease = context.getters['getLease']
        lease.tenant[0].files.push(response.data.data)

        context.commit('setSelected', lease)

        alert.success('Document uploaded successfully.')
      } else {
        alert.error(response.data.errmsg)
      }
    } catch (error) {
      alert.error(
        'Sorry, something went wrong and we cannot upload file. Please reload your browser and try again'
      )
    }
  },
  async deleteFile(context, { id, tenantId }) {
    if (!confirm('Are you sure?')) {
      return false
    }

    try {
      const response = await axios.post(
        config.apiBaseUrl + 'house/deleteContractFile',
        { id, xId: tenantId, type: 'tenant' }
      )

      if (response.data.errno === 0) {
        let lease = context.getters['getLease']

        lease.tenant[0].files.forEach((file, index) => {
          if (file.id === id) {
            lease.tenant[0].files.splice(index, 1)
          }
        })

        context.commit('setSelected', lease)
        alert.success('Successfully deleted contract file')
      } else {
        alert.error(response.data.errmsg)
      }
    } catch (error) {
      alert.error(error)
    }
  },
  async savePayment(context, payment) {
    try {
      const response = await axios.put(
        config.apiBaseUrl + 'payment/' + payment.id, payment
      )

      if (response.data.errno === 0) {
        // console.log('updatePayments', [payment])
        context.commit('updatePayments', [payment])

        // Update the payment fees
        if (payment.fee.length > 0) {
          payment.fee.forEach((fee) => {
            fee.payment_id = payment.id
            fee.state = 1
            context.dispatch('HouseList/Fees/update', {fee}, {
              root: true,
            })
          })
        }

        alert.success('Payment saved successfully')
      } else {
        alert.error(response.data.errmsg)
      }
    } catch (error) {
      alert.error(
        'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
      )
    }
  },
  async bulkUpdatePayments(context, payments) {
    try {
      const paymentResponse = await axios.put(
        config.apiBaseUrl + 'payment/putMany',
        payments
      )

      if (paymentResponse.data.errno === 0) {
        context.commit('updatePayments', payments)
        alert.success('Payments are processed successfully')
      } else {
        alert.error(paymentResponse.data.errmsg)
      }
    } catch (error) {
      alert.error(
        'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
      )
    }
  },
  async downloadPayment(context, payment) {
    try {
      await axios
        .post(
          config.apiBaseUrl + 'payment/downloadPDF',
          { id: payment.id },
          { responseType: 'blob' }
        )
        .then((response) => {
          let blob = new Blob([response.data], { type: 'application/pdf' })
          const url = window.URL.createObjectURL(blob)

          let newTab = window.open()
          newTab.location.href = url
          setTimeout(() => {
            if (newTab.closed) {
              let link = document.createElement('a')
              link.href = url
              link.download = payment.id + '.pdf'
              // document.body.appendChild(link)
              link.click()
            }
          }, 200)
        })
    } catch (error) {
      alert.error(
        'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
      )
    }
  },
  async sendPayment(context, payment_properties) {
    try {
      const response = await axios.post(
        config.apiBaseUrl + 'payment/sendPayment',
        { data: [payment_properties] }
      )

      if (response.data.errno === 0) {
        context.commit('updateSelectedPayment', { ...payment_properties, payment_pdf_send: 1})
        alert.success('Report sent successfully to the owner')
      } else {
        alert.error(response.data.errmsg)
      }
    } catch (error) {
      alert.error(
        'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
      )
    }
  },
  async redoPayment(context, payment) {
    // console.log({ Lease_redoPayment: payment })
    try {
      if (payment.fee.length > 0) {
        payment.fee.forEach((fee) => {
          fee.payment_id = ''
          fee.state = 0
          context.dispatch(
            'HouseList/Fees/update',
            { fee: fee },
            { root: true }
          )
        })
      }

      payment.state = '0'
      payment.pay_time = null
      payment.fee = []

      const response = await axios.put(
        config.apiBaseUrl + 'payment/' + payment.id,
        payment
      )

      if (response.data.errno === 0) {
        context.commit('updatePayments', [payment])
        alert.success('Payment is reverted to unprocessed state')
      } else {
        alert.error(response.data.errmsg)
      }
    } catch (error) {
      alert.error(
        'Sorry, we are unable to complete your request. Please refresh your browser and try again.'
      )
    }
  },
}

const storeModule = {
  namespaced: true,
  state: state,
  getters: getters,
  actions: actions,
  mutations: mutations,
}

export default storeModule
